import "./home.scss";
import { InputText } from "primereact/inputtext";
import { useCallback, useState } from "react";
import HeaderContent from "@shared/ui/header-content/header-content";
import { Dropdown } from "primereact/dropdown";
import { useUserIdentity } from "@core/services/user-identity.hook";
import { useFormDropdownBranchContext } from "@domain/branch/form-dropdown-branch/form-dropdown-branch.context";
import {
  useQyGetReportBranch,
  useQyGetReportPerformance,
} from "@core/query/report.query";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { CalendarDate } from "@core/model/calendar-event.interface";
import CardStats from "./card-stats/card-stats";
import { SkeletonList } from "@shared/ui/skeleton-list/skeleton-list";
import { Button } from "primereact/button";
import RankingStats from "./ranking-stats/ranking-stats";
import {
  EmployeePerformanceModel,
  GetBranchDailyModelV2,
  GetDailyPerformanceModel,
} from "@core/model/query-model-report";
import { Outlet, useNavigate } from "react-router-dom";
import { ALL } from "@core/model/const";

/* eslint-disable-next-line */
export interface HomeProps {}

const getDefaultBranchId = (isAdmin: boolean, branchId: string) => {
  return isAdmin ? undefined : branchId;
};

export function Home() {
  const navigate = useNavigate();
  const { formatDate } = useDateTimeContext();
  const { getUser, userIsManagement } = useUserIdentity();
  const [calendarDate, setCalendarDate] = useState<CalendarDate>({
    start: formatDate(new Date()),
    end: formatDate(new Date()),
  });
  const [selectedBranch, setSelectedBranch] = useState(
    getDefaultBranchId(userIsManagement(), getUser()?.branchId),
  );
  const { mappedBranch } = useFormDropdownBranchContext();
  const [report, setReport] = useState<GetBranchDailyModelV2>();
  const [performanceRankingModel, setPerformanceRankingModel] = useState<
    Record<string, EmployeePerformanceModel[]>
  >({} as Record<string, EmployeePerformanceModel[]>);


  // QUERIES
  // Get report branch daily
  const handleReportBranchDailySuccess = (data: GetBranchDailyModelV2) => {
    setReport(data);
  };
  const { mutateAsync: getReportBranch, isLoading } = useQyGetReportBranch(
    handleReportBranchDailySuccess,
  );

  // Get report performance ranking
  const handleReportPerformanceSuccess = (data: GetDailyPerformanceModel) => {
    if (data === undefined) {
      setPerformanceRankingModel({});
      return;
    }

    setPerformanceRankingModel(
      data as unknown as Record<string, EmployeePerformanceModel[]>,
    );
  };
  const { mutateAsync: getReportPerformance, isLoading: isPerformanceLoading } =
    useQyGetReportPerformance(handleReportPerformanceSuccess);

  // Load reports on page mount
  // useEffect(() => {
  //   if (hasInitialized.current) return;
  //   hasInitialized.current = true;

  //   const _selectedBranch = selectedBranch === "ALL" ? '' : selectedBranch ?? '';
  //   getReportBranch({
  //     startDate: calendarDate.start,
  //     endDate: calendarDate.end,
  //     branchId: _selectedBranch,
  //   });
  //   getReportPerformance({
  //     startDate: calendarDate.start,
  //     endDate: calendarDate.end,
  //     branchId: _selectedBranch,
  //   });
  // }, [getReportBranch, getReportPerformance]);

  // LOCAL FUNCTIONS
  const clearFilter = () => {
    setCalendarDate({
      start: formatDate(new Date()),
      end: formatDate(new Date()),
    });
    setSelectedBranch(
      getDefaultBranchId(userIsManagement(), getUser()?.branchId),
    );
  };

  // HTML RENDER
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const branchElement = (
    <div className="flex flex-col">
      <label className="text-xs text-gray-500" htmlFor="start">
        Branch
      </label>
      <Dropdown
        value={selectedBranch}
        onChange={(e) => {
          if (e.value === ALL) {
            setSelectedBranch("asdfsadf");
          }
          setSelectedBranch(e.value);
        }}
        options={mappedBranch}
        optionLabel="label"
        placeholder="Select a branch"
        className="w-full md:w-14rem"
        filter
      />
    </div>
  );
  const dateElement = (
    <div className="flex gap-4 items-center">
      <div className="flex flex-col">
        <label className="text-xs text-gray-500" htmlFor="start">
          Start
        </label>
        <InputText
          type="date"
          id="start"
          value={calendarDate.start}
          onChange={(e) => {
            setCalendarDate({
              ...calendarDate,
              start: e.target.value,
            });
          }}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-500" htmlFor="end">
          End
        </label>
        <InputText
          type="date"
          id="end"
          value={calendarDate.end}
          onChange={(e) => {
            setCalendarDate({
              ...calendarDate,
              end: e.target.value,
            });
          }}
        />
      </div>
    </div>
  );
  const filterElement = (
    <div className="flex gap-4 items-end mb-2">
      {userIsManagement() && <div>{branchElement}</div>}
      {dateElement}
      <div className="flex gap-2">
        <Button
          label="Apply"
          onClick={() => {
            const _selectedBranch = selectedBranch === "ALL" ? '' : selectedBranch ?? '';
            getReportBranch({
              startDate: calendarDate.start,
              endDate: calendarDate.end,
              branchId: _selectedBranch,
            });
            getReportPerformance({
              startDate: calendarDate.start,
              endDate: calendarDate.end,
              branchId: _selectedBranch,
            });
          }}
          className="block mb-1"
        />
        <Button
          label="Clear"
          severity="secondary"
          onClick={() => clearFilter()}
          className="block mb-1"
          outlined
        />
      </div>
    </div>
  );
  const getBranchName = useCallback(() => {
    if (userIsManagement()) {
      return "Dashboard";
    }

    if (mappedBranch.length === 0) {
      return "Dashboard";
    }

    const selectedBranchLabelValue = mappedBranch.find(
      (item) => item.value === selectedBranch,
    );
    const branchLabel = selectedBranchLabelValue?.label || "";
    const label = branchLabel ? `${branchLabel} Branch Dashboard` : "Dashboard";
    return label;
  }, [mappedBranch, userIsManagement, selectedBranch]);
  const handleRankingNavigate = (title: string) => {
    const branchParam = selectedBranch ? `branchId=${selectedBranch}` : "";

    return navigate(
      `ranking?type=${title}&startDate=${calendarDate.start}&endDate=${calendarDate.end}${branchParam}`,
    );
  };

  // LOCAL RENDER
  const main = (
    <section>
      {isLoading ? displayLoading : <CardStats data={report} />}

      {isPerformanceLoading ? (
        displayLoading
      ) : (
        <RankingStats
          data={performanceRankingModel}
          rankingNavigate={handleRankingNavigate}
        />
      )}
    </section>
  );

  return (
    <div id="Home" className="home">
      <HeaderContent title={getBranchName()}></HeaderContent>
      <div className="p-7">
        {filterElement}
        {main}

        <Outlet />
      </div>
    </div>
  );
}

export default Home;
